import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { getClientStats, getChallenges, getClientSessions, selectCategory } from '../lib/api';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import BeltProgress from '../components/BeltProgress';
import EditClientProfileDialog from '../components/EditClientProfileDialog';
import RewardsPanel from '../components/RewardsPanel';
import {
  Target, Award, Calendar, TrendingUp, ChevronRight,
  Dumbbell, Shield as ShieldIcon, Users, Pencil,
} from 'lucide-react';
import { CATEGORY_NAMES, formatDate, formatTime, getInitials } from '../lib/utils';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const ClientDashboard = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);

  const profile = user?.client_profile || {};

  const loadData = useCallback(async () => {
    try {
      const [statsRes, challengesRes, sessionsRes] = await Promise.all([
        getClientStats(),
        getChallenges(),
        getClientSessions(),
      ]);
      setStats(statsRes.data);
      setChallenges(challengesRes.data.challenges || []);
      setSessions(sessionsRes.data || []);
    } catch (error) {
      console.error('Failed to load dashboard data', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSelectCategory = async (category) => {
    try {
      await selectCategory(category);
      await refreshUser();
      loadData();
      toast.success(`Category set to ${CATEGORY_NAMES[category]}`);
    } catch (error) {
      toast.error('Failed to select category');
    }
  };

  // Disciplines the client trains — from belt_progress + onboarding + current_category
  const disciplines = useMemo(() => {
    const set = new Set();
    Object.keys(profile.belt_progress || {}).forEach((k) => {
      const disc = k.includes(':') ? k.split(':').pop() : k;
      if (disc) set.add(disc);
    });
    if (profile.current_category) set.add(profile.current_category);
    if (profile.onboarding_discipline) set.add(profile.onboarding_discipline);
    return Array.from(set);
  }, [profile]);

  const categories = [
    { id: 'personal_training', name: 'Personal Training', icon: Dumbbell, color: 'text-green-400' },
    { id: 'self_defense', name: 'Self Defense', icon: ShieldIcon, color: 'text-blue-400' },
    { id: 'jiujitsu', name: 'Jiu-Jitsu', icon: Users, color: 'text-purple-400' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  const upcomingSessions = sessions
    .filter((s) => s.status === 'confirmed' || s.status === 'pending')
    .slice(0, 3);

  const beltRankLabel = `${(profile.current_phase || 'white').replace(/\b\w/g, (c) => c.toUpperCase())} Belt`;

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="client-dashboard">
      <div className="max-w-7xl mx-auto">
        {/* Instagram-style Profile Header */}
        <div
          className="mb-10 bg-[#18181B] border border-zinc-800 rounded-lg p-6 sm:p-8"
          data-testid="client-profile-header"
        >
          <div className="flex flex-col sm:flex-row gap-6 sm:gap-10">
            {/* Avatar */}
            <div className="flex flex-col items-center sm:items-start shrink-0">
              <Avatar className="w-28 h-28 sm:w-32 sm:h-32 border-2 border-[#C0A062]/40">
                <AvatarImage src={profile.profile_image} alt={user?.full_name} />
                <AvatarFallback className="bg-[#27272A] text-[#C0A062] text-3xl font-bold">
                  {getInitials(user?.full_name || 'C')}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Meta */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <h1
                  className="text-2xl sm:text-3xl font-bold text-white"
                  data-testid="client-profile-name"
                >
                  {user?.full_name}
                </h1>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-zinc-700 text-zinc-200 hover:bg-zinc-800"
                  onClick={() => setEditOpen(true)}
                  data-testid="client-edit-profile-btn"
                >
                  <Pencil className="w-3.5 h-3.5 mr-2" />
                  Edit profile
                </Button>
              </div>

              {/* Basic stats row */}
              <div className="flex flex-wrap gap-6 sm:gap-10 mb-5" data-testid="client-profile-stats">
                <div>
                  <span className="text-white font-bold text-lg">
                    {stats?.completed_sessions || 0}
                  </span>{' '}
                  <span className="text-zinc-400 text-sm">sessions</span>
                </div>
                <div>
                  <span className="text-white font-bold text-lg">
                    {stats?.completed_challenges || 0}
                  </span>{' '}
                  <span className="text-zinc-400 text-sm">challenges</span>
                </div>
                <div>
                  <span className="text-white font-bold text-lg">{stats?.total_points || 0}</span>{' '}
                  <span className="text-zinc-400 text-sm">points</span>
                </div>
              </div>

              {/* Belt rank */}
              <div className="flex items-center gap-2 mb-3" data-testid="client-belt-rank-pill">
                <Award className="w-4 h-4 text-[#C0A062]" />
                <span className="text-[#C0A062] font-semibold text-sm">Belt Rank:</span>
                <span className="text-white font-bold text-sm capitalize font-accent">
                  {beltRankLabel}
                </span>
              </div>

              {/* Disciplines */}
              {disciplines.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4" data-testid="client-disciplines">
                  {disciplines.map((d) => (
                    <Badge
                      key={d}
                      className="bg-zinc-800 text-zinc-200 border border-zinc-700 text-xs px-2 py-0.5"
                    >
                      {CATEGORY_NAMES[d] || d}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Bio */}
              {profile.bio ? (
                <p
                  className="text-zinc-300 text-sm whitespace-pre-line max-w-2xl"
                  data-testid="client-profile-bio"
                >
                  {profile.bio}
                </p>
              ) : (
                <button
                  type="button"
                  onClick={() => setEditOpen(true)}
                  className="text-sm text-zinc-500 hover:text-[#C0A062]"
                  data-testid="client-profile-bio-empty"
                >
                  + Add a bio
                </button>
              )}
            </div>
          </div>
        </div>

        {/* DG Points + favorites + referral */}
        <RewardsPanel />

        {/* Category Selection (if not selected) */}
        {!profile.current_category && (
          <div className="mb-8 p-6 bg-[#18181B] border border-zinc-800 rounded-lg" data-testid="category-selection">
            <h2 className="text-xl font-bold text-white mb-4">Choose Your Training Path</h2>
            <p className="text-zinc-400 mb-6">Select a category to start tracking your progress</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => handleSelectCategory(cat.id)}
                  className="p-6 bg-zinc-800/50 border border-zinc-700 rounded-lg hover:border-[#C0A062]/50 transition-colors text-left group"
                  data-testid={`select-category-${cat.id}`}
                >
                  <cat.icon className={`w-8 h-8 ${cat.color} mb-3`} />
                  <h3 className="text-lg font-bold text-white">{cat.name}</h3>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="stats-card p-6 rounded-lg" data-testid="stat-belt-rank">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-sm">Belt Rank</span>
              <Award className="w-5 h-5 text-[#C0A062]" />
            </div>
            <p className="text-2xl font-bold text-white capitalize font-accent">
              {beltRankLabel}
            </p>
          </div>

          <div className="stats-card p-6 rounded-lg" data-testid="stat-points">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-sm">Total Points</span>
              <TrendingUp className="w-5 h-5 text-[#C0A062]" />
            </div>
            <p className="text-2xl font-bold text-white">{stats?.total_points || 0}</p>
          </div>

          <div className="stats-card p-6 rounded-lg" data-testid="stat-sessions">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-sm">Sessions This Rank</span>
              <Calendar className="w-5 h-5 text-[#C0A062]" />
            </div>
            <p className="text-2xl font-bold text-white">{stats?.completed_sessions || 0} / 12</p>
          </div>

          <div className="stats-card p-6 rounded-lg" data-testid="stat-challenges">
            <div className="flex items-center justify-between mb-2">
              <span className="text-zinc-400 text-sm">Challenges Done</span>
              <Target className="w-5 h-5 text-[#C0A062]" />
            </div>
            <p className="text-2xl font-bold text-white">
              {stats?.completed_challenges || 0} / {stats?.total_challenges || 0}
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Merged Progress + Challenges */}
          <div className="lg:col-span-2 space-y-8">
            <div
              className="bg-[#18181B] border border-zinc-800 rounded-lg p-6"
              data-testid="progress-challenges-card"
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Your Progress &amp; Challenges</h2>
                {profile.current_category && (
                  <span className="text-sm text-[#C0A062] font-medium">
                    {CATEGORY_NAMES[profile.current_category]}
                  </span>
                )}
              </div>

              <BeltProgress currentPhase={profile.current_phase || 'white'} showLabels size="lg" />

              <div className="mt-6 p-4 bg-zinc-800/30 rounded-lg" data-testid="next-belt-requirements">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-zinc-400">Sessions to next rank</span>
                  <span className="text-sm text-white font-medium">
                    {Math.max(0, 12 - (stats?.completed_sessions || 0))} remaining
                  </span>
                </div>
                <Progress
                  value={((stats?.completed_sessions || 0) / 12) * 100}
                  className="h-2 bg-zinc-800"
                />
                <p className="text-xs text-zinc-500 mt-3">
                  Next belt requirements: {Math.max(0, 12 - (stats?.completed_sessions || 0))} more
                  sessions needed · Coach sign-off · Skill challenge
                </p>
              </div>

              {/* Active Challenges — merged */}
              <div className="mt-8 pt-6 border-t border-zinc-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white">Active Challenges</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-[#C0A062]"
                    onClick={() => navigate('/challenges')}
                    data-testid="view-all-challenges-btn"
                  >
                    View All <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>

                {challenges.length === 0 ? (
                  <p className="text-zinc-400 text-center py-6 text-sm">
                    {profile.current_category
                      ? 'Loading challenges...'
                      : 'Select a category to see challenges'}
                  </p>
                ) : (
                  <div className="space-y-3">
                    {challenges.slice(0, 4).map((challenge) => (
                      <div
                        key={challenge.id}
                        className={`challenge-card p-4 bg-zinc-800/30 border border-zinc-700 rounded-lg ${
                          challenge.is_completed ? 'completed' : ''
                        }`}
                        data-testid={`challenge-${challenge.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h3 className="font-medium text-white">{challenge.name}</h3>
                            <p className="text-sm text-zinc-400">{challenge.description}</p>
                          </div>
                          <div className="text-right">
                            <span className="text-[#C0A062] font-bold">+{challenge.points}</span>
                            <p className="text-xs text-zinc-500">points</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Upcoming Sessions */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="upcoming-sessions">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-white">Upcoming Sessions</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-[#C0A062]"
                  onClick={() => navigate('/sessions')}
                >
                  All <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </div>

              {upcomingSessions.length === 0 ? (
                <div className="text-center py-8">
                  <Calendar className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 text-sm mb-4">No upcoming sessions</p>
                  <Button
                    size="sm"
                    className="btn-primary"
                    onClick={() => navigate('/trainers')}
                    data-testid="book-session-btn"
                  >
                    Book a Session
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {upcomingSessions.map((session) => (
                    <div
                      key={session.id}
                      className="session-card p-4 rounded-lg"
                      data-testid={`session-${session.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-white">
                          {CATEGORY_NAMES[session.category]}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded ${
                          session.status === 'confirmed'
                            ? 'bg-green-500/10 text-green-400'
                            : 'bg-yellow-500/10 text-yellow-400'
                        }`}>
                          {session.status}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-400">
                        {formatDate(session.scheduled_date)} at {formatTime(session.scheduled_time)}
                      </p>
                      <p className="text-xs text-zinc-500 mt-1">
                        with {session.trainer?.full_name}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Quick Actions */}
            <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6">
              <h2 className="text-lg font-bold text-white mb-4">Quick Actions</h2>
              <div className="space-y-3">
                <Button
                  className="w-full btn-primary justify-start"
                  onClick={() => navigate('/trainers')}
                  data-testid="find-trainer-btn"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Find a Trainer
                </Button>
                <Button
                  variant="outline"
                  className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800 justify-start"
                  onClick={() => navigate('/sessions')}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  View Sessions
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <EditClientProfileDialog open={editOpen} onOpenChange={setEditOpen} />
    </div>
  );
};

export default ClientDashboard;
